"use client";

import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";

import { FaPlay } from "react-icons/fa";
import { ImTicket } from "react-icons/im";
import { MdLocalMovies } from "react-icons/md";
import { BannerCarouselItemProps } from "../../../@types/Carousel";
import ShowCaseTrailerModal from "../../../modules/ShowCaseTrailerModal";
import GradeAWarn from "../../../modules/WarningModules/gradeAWarn";
import Image from "../../Image";

function BannerCarouselItem({
  bannerImg,
  showBannerAction,
  showBuyButton = false,
  bannerActionElem,
  index,
  onBuyClick,
  grade = "PG",
  gradientColor,
  imageContainerProps,
  trailer,
  isTrailerOnCenter,
  hideTrailer = false,
  bannerBoxProps,
  imageProps,
  // Overlay props — destructured so they do not fall into ...props
  bannerTitle,
  genre,
  description,
  showTitle = false,
  showGenre = false,
  showDescription = false,
  genreProps,
  titleProps,
  descriptionProps,
  buyButtonProps,
  playIconProps,
  buyButtonTextProps,
  playIconTextProps,
  gradeBadgeProps,
  actionVariant = "default",
  bannerOverlayProps,
  hasTextOverlay = false,
  ...props
}: BannerCarouselItemProps & {
  index: number;
  onBuyClick?: () => void;
  gradientColor?: string;
  hideTrailer?: boolean;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box
      key={`banner_carousel_item_${index}`}
      w="full"
      borderRadius="1rem"
      overflow="hidden"
      position="relative"
      width="100%"
      aspectRatio={"2.7 / 1"}
      {...props}
      sx={{ isolation: "isolate" }}
    >
      {/* Blurred background fill — original */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundImage={`url(${bannerImg})`}
        backgroundSize="cover"
        backgroundPosition="center"
        filter="blur(8px)"
        transform="scale(1.1)"
        opacity={0.8}
      />

      {/* Main image — original */}
      <Box
        position="relative"
        zIndex={1}
        height="full"
        {...imageContainerProps}
      >
        <Image
          objectFit="fill"
          w="full"
          h="full"
          objectPosition="center"
          src={bannerImg}
          fallbackSrc="/img/pv_logo_landscape.png"
          alt={`banner_slide_${index}`}
          {...imageProps}
        />
        {gradientColor && (
          <Box
            position="absolute"
            top="0"
            left="0"
            w="full"
            h="full"
            bg={gradientColor}
            zIndex="1"
          />
        )}
      </Box>

      {/* Functional gradient scrim — only when text overlay content is present.
          Dark-to-transparent gradient is not a brand choice; it exists solely
          to keep white text legible over any banner image. */}
      {hasTextOverlay && (
        <Box
          position="absolute"
          bottom="0"
          left="0"
          w="100%"
          h="80%"
          bgGradient="linear(to-t, blackAlpha.900 0%, blackAlpha.700 50%, transparent 100%)"
          zIndex={2}
          pointerEvents="none"
          {...bannerOverlayProps}
        />
      )}

      {/* Centered Trailer Button — original, unchanged */}
      {isTrailerOnCenter && trailer && (
        <Flex
          position="absolute"
          top="0"
          left="0"
          w="full"
          h="full"
          zIndex={2}
          justifyContent="center"
          alignItems="center"
          pointerEvents="none"
        >
          <Box pointerEvents="auto">
            <ShowCaseTrailerModal
              url={trailer}
              actionEl={
                <IconButton
                  size="lg"
                  isRound
                  variant="solid"
                  colorScheme="whiteAlpha"
                  fontSize="2xl"
                  aria-label="Play Trailer"
                  icon={<FaPlay />}
                  sx={{
                    width: "60px",
                    height: "60px",
                    bg: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(4px)",
                    _hover: {
                      bg: "rgba(255, 255, 255, 0.3)",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.3s ease",
                  }}
                />
              }
            />
          </Box>
        </Flex>
      )}

      {showBannerAction && (
        <Box
          position="absolute"
          bottom="1rem"
          left="1rem"
          zIndex={3}
          {...bannerBoxProps}
        >
          {showGenre && genre && <Box {...genreProps}>{genre}</Box>}
          {showTitle && bannerTitle && <Box {...titleProps}>{bannerTitle}</Box>}
          {showDescription && description && (
            <Box {...descriptionProps}>{description}</Box>
          )}

          <Flex
            gap={{ base: 2, md: 3 }}
            mt={hasTextOverlay ? 2 : 0}
            alignItems="center"
          >
            {actionVariant === "pill" ? (
              <>
                {grade && gradeBadgeProps && (
                  <Box {...gradeBadgeProps}>{grade}</Box>
                )}
                {trailer && !hideTrailer && !isTrailerOnCenter && (
                  <ShowCaseTrailerModal
                    url={trailer}
                    actionEl={
                      <Button
                        leftIcon={<FaPlay size={11} />}
                        {...(playIconProps as any)}
                      >
                        Play Trailer
                      </Button>
                    }
                  />
                )}
                {showBuyButton && (
                  <Button
                    leftIcon={<MdLocalMovies size={15} />}
                    onClick={() => {
                      if (grade === "A") {
                        onOpen();
                      } else {
                        onBuyClick?.();
                      }
                    }}
                    {...(buyButtonProps as any)}
                  >
                    View Detail
                  </Button>
                )}
              </>
            ) : (
              <>
                {grade && gradeBadgeProps && (
                  <Box {...gradeBadgeProps}>{grade}</Box>
                )}
                {showBuyButton && (
                  <Flex
                    as={Button}
                    onClick={() => {
                      if (grade === "A") {
                        onOpen();
                      } else {
                        onBuyClick?.();
                      }
                    }}
                    alignItems="center"
                    gap={{ base: 2, md: 4 }}
                    background="rgba(0,0,0,0.3)"
                    _hover={{
                      background: "rgba(0,0,0,0.3)",
                    }}
                    borderRadius={{ base: "8px", md: "13px" }}
                    px={{ base: "0.8rem", md: "1rem" }}
                    py={{ base: "0.6rem", md: "2.2rem" }}
                    cursor="pointer"
                    role="group"
                    color="white"
                    zIndex={500}
                    {...(buyButtonProps as any)}
                  >
                    <IconButton
                      size={{ base: "xs", md: "lg" }}
                      _groupHover={{
                        bg: "primary.500",
                        borderColor: "primary.500",
                      }}
                      transition="all 0.6s ease"
                      borderRadius="full"
                      variant="outline"
                      fontSize={{ base: "xs", md: "2xl" }}
                      aria-label="Ticket"
                      icon={<ImTicket />}
                    />
                    <Text
                      fontSize={{ base: "xs", md: "xl" }}
                      fontWeight={600}
                      textTransform="uppercase"
                      color="surface.100"
                      {...buyButtonTextProps}
                    >
                      Buy Now
                    </Text>
                  </Flex>
                )}
                {trailer && !hideTrailer && !isTrailerOnCenter && (
                  <ShowCaseTrailerModal
                    url={trailer}
                    actionEl={
                      <Flex
                        as={Button}
                        alignItems="center"
                        gap={{ base: 2, md: 4 }}
                        background="rgba(0,0,0,0.3)"
                        _hover={{
                          background: "rgba(0,0,0,0.3)",
                        }}
                        borderRadius={{ base: "8px", md: "13px" }}
                        px={{ base: "0.8rem", md: "1rem" }}
                        py={{ base: "0.6rem", md: "2.2rem" }}
                        cursor="pointer"
                        role="group"
                        color="white"
                        zIndex={500}
                        {...(playIconProps as any)}
                      >
                        <IconButton
                          size={{ base: "xs", md: "lg" }}
                          _groupHover={{
                            bg: "primary.500",
                            borderColor: "primary.500",
                          }}
                          transition="all 0.6s ease"
                          borderRadius="full"
                          variant="outline"
                          fontSize={{ base: "xs", md: "2xl" }}
                          aria-label="Play Trailer"
                          icon={<FaPlay />}
                        />
                        <Text
                          fontSize={{ base: "xs", md: "xl" }}
                          fontWeight={600}
                          textTransform="uppercase"
                          color="surface.100"
                          {...playIconTextProps}
                        >
                          View Trailer
                        </Text>
                      </Flex>
                    }
                  />
                )}
              </>
            )}
          </Flex>

          <>{bannerActionElem}</>
          <GradeAWarn
            isOpen={isOpen}
            onClose={onClose}
            onProceed={onBuyClick as () => void}
            modalKey={"warning_module_banner"}
          />
        </Box>
      )}
    </Box>
  );
}

export default BannerCarouselItem;
